import React, { useState, useMemo, useCallback } from "react";
import AdminPageLayout from "../../../components/FullPageLayout/AdminPageLayout";
import { Box, Button } from "@mui/material";

import { v4 as uuid } from "uuid";

import { Add, SystemUpdate, Print, ImportExport } from "@mui/icons-material";
import { PropTypes } from "prop-types";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import isEqual from "lodash/isEqual";
import ContentOwnerIcon from "../../../components/Icons/ContentOwnerIcon";
import CourseFlags from "../../../components/Icons/CourseFlags";
import CourseTypeIcon from "../../../components/Icons/CourseTypeIcon";
import ButtonGroup from "../../../components/Buttons/ButtonGroup";
import PoliciesFilters from "./PoliciesFilters";
import Table from "../../../components/ReactTable/Table";
import useControlledRowSelection from "../../../components/ReactTable/useControlledRowSelection";
import { useTableData } from "../../../components/ReactTable/useTableData";
import usePermissions from "../../../redux/selectors/usePermissions";
import useTableState from "../../../hooks/useTableState";
import usePolicies from "../../../hooks/policies";
import { buildPolicyDetailsRoute } from "../../../routes/AdminRoutes";
import useModal from "../../../hooks/useModal";
import AddNewPolicyModal from "./AddNewPolicyModal";

const getTableId = () => uuid();
const initialSortBy = [{ id: "name", desc: false }];

const PolicyNameCell = ({ name, flags, contentOwner, clientOwned }) => {
  return (
    <Box display="flex" alignItems="center">
      <Box mr={2}>{name}</Box>
      {flags && <CourseFlags flags={flags} />}
      {contentOwner && (
        <ContentOwnerIcon
          contentOwner={contentOwner}
          clientOwned={clientOwned}
        />
      )}
    </Box>
  );
};

PolicyNameCell.propTypes = {
  flags: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string
};

const columns = t => [
  {
    Header: t("table.columnHeader.Type"),
    accessor: "type",
    Cell: ({ cell: { value } }) => <CourseTypeIcon type={value} />,
    id: "type"
  },
  {
    Header: t("table.columnHeader.Number"),
    accessor: "number",
    id: "number"
  },
  {
    Header: t("table.columnHeader.Name"),
    accessor: "name",
    id: "name"
  },
  {
    Header: t("table.columnHeader.Manual"),
    accessor: "manualNames",
    Cell: ({ cell: { value } }) => value.join(", "),
    id: "manualNames"
  },
  {
    Header: t("table.columnHeader.Assigned"),
    accessor: "assigned",
    id: "assigned"
  },
  {
    Header: t("table.columnHeader.Overdue"),
    accessor: "overdue",
    id: "overdue"
  },
  {
    Header: t("table.columnHeader.Views"),
    accessor: "completed",
    id: "completed"
  }
];

const Policies = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const openAddNewPolicyModal = useModal(AddNewPolicyModal);
  const { canAddPolicy } = usePermissions();
  const tableState = useTableState("policies");
  const tableId = getTableId();

  // setPoliciesFilter will be useful later
  // eslint-disable-next-line
  const [policiesFilters, setPoliciesFilters] = useState(tableState.filters);

  const { fetchPolicies, createPolicy, isLoading } = usePolicies();

  const lazyFetch = data => {
    return fetchPolicies(data);
  };

  const {
    selectedRows,
    updateControlledSelection
  } = useControlledRowSelection();

  const {
    data: policies,
    paginationState,
    onStateChange,
    isFetching,
    onApplyExternalFilters
  } = useTableData(lazyFetch, {
    externalFilters: policiesFilters,
    sortBy: initialSortBy
  });

  const handleOpenModalClick = useCallback(async () => {
    const policy = await openAddNewPolicyModal({ createPolicy }).result;
    if (policy) {
      dispatch(push(buildPolicyDetailsRoute(policy.id)));
    }
  }, []);

  const memoColumns = useMemo(() => columns(t), []);

  const handleRowClick = rowData => {
    dispatch(push(buildPolicyDetailsRoute(rowData.row.original.id)));
  };

  const handleApplyFilters = newFilters => {
    const filters = {
      ...policiesFilters,
      ...newFilters
    };

    if (!isEqual(policiesFilters, filters)) {
      setPoliciesFilters(filters);
      onApplyExternalFilters(filters);
    }
  };

  return (
    <AdminPageLayout title={t("managePolicies.policiesTitle")}>
      <Box mb={3} mt={0}>
        <PoliciesFilters
          defaultValues={policiesFilters}
          onApplyFilters={handleApplyFilters}
          expandedByDefault={true}
        >
          {canAddPolicy && (
            <div style={{ width: "100%" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleOpenModalClick}
                  data-qa="add-policy-btn"
                >
                  {t("managePolicies.addPolicyButton")}
                </Button>
                <ButtonGroup align="right" flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SystemUpdate />}
                    onClick={() => openAddNewPolicyModal()}
                    data-qa="add-policy-btn"
                  >
                    {t("managePolicies.inactivateButton")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Print />}
                    onClick={() => openAddNewPolicyModal()}
                    data-qa="add-policy-btn"
                  >
                    {t("managePolicies.printButton")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ImportExport />}
                    onClick={() => openAddNewPolicyModal()}
                    data-qa="add-policy-btn"
                  >
                    {t("managePolicies.exportButton")}
                  </Button>
                </ButtonGroup>
              </Box>
            </div>
          )}
        </PoliciesFilters>
        <Table
          key={tableId}
          data={policies}
          columns={memoColumns}
          onStateChange={({ selectedRowIds, ...state }) => onStateChange(state)}
          paginationState={paginationState}
          paginated
          loading={isFetching || isLoading}
          showSelectAllCheckbox
          showRowSelection={policiesFilters?.status !== "active_unsubscribed"}
          initialSortBy={initialSortBy}
          controlledSelectedRows={selectedRows}
          updateControlledSelection={updateControlledSelection}
          onRowClick={(e, rowData) => handleRowClick(rowData)}
          disableRowClick={policiesFilters?.status === "active_unsubscribed"}
        />
      </Box>
    </AdminPageLayout>
  );
};

Policies.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      policyId: PropTypes.string
    })
  })
};

export default Policies;
